import {
  appendOne,
  buildWireOwnershipControlDeclaration,
} from "../Authentication/clientOnboardingSubmitHelpers";

export function normalizeCompanyWebsiteUrl(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed || trimmed === "https://" || trimmed === "http://") {
    return "";
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

export function ensureCompanyWebsiteProtocol(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return "https://";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function filenameFromStoredPath(path) {
  if (!path) return "";
  const s = String(path);
  const withoutQuery = s.split("?")[0];
  const parts = withoutQuery.split("/").filter(Boolean);
  return parts[parts.length - 1] || s;
}

export function parseStoredFileList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {
      return [trimmed];
    }
    return [trimmed];
  }
  return [];
}

/** API stores insurance policies as arrays; the product allows one file per type. */
export function firstStoredFilePath(value) {
  const list = parseStoredFileList(value);
  return list[0] || "";
}

function emptyDirector() {
  return {
    fullName: "",
    idFile: null,
    proofOfAddressFile: null,
    existingIdUrl: "",
    existingProofUrl: "",
  };
}

function emptyOwner() {
  return {
    fullName: "",
    percentageOfOwnership: "",
    idFile: null,
    proofOfAddressFile: null,
    existingIdUrl: "",
    existingProofUrl: "",
  };
}

function mapDirectorFromApi(fullName, idUrls, proofUrls, index) {
  return {
    fullName: fullName || "",
    idFile: null,
    proofOfAddressFile: null,
    existingIdUrl: idUrls[index] || idUrls[0] || "",
    existingProofUrl: proofUrls[index] || proofUrls[0] || "",
  };
}

export function hydrateOwnershipControlDeclaration(ocd = {}) {
  const idUrls = parseStoredFileList(ocd.directorIdFiles);
  const proofUrls = parseStoredFileList(ocd.directorProofOfAddressFiles);

  const nameParts = String(ocd.directorsOrBeneficialOwners || "")
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);

  let directors;
  if (nameParts.length) {
    directors = nameParts.map((fullName, idx) =>
      mapDirectorFromApi(fullName, idUrls, proofUrls, idx)
    );
  } else if (idUrls.length || proofUrls.length) {
    directors = [mapDirectorFromApi("", idUrls, proofUrls, 0)];
  } else {
    directors = [emptyDirector()];
  }

  const rawType = ocd.ownershipCompanyType;
  const ownershipCompanyType =
    rawType === "individual" || rawType === "individuals"
      ? "individuals"
      : rawType === "corporate"
        ? "corporate"
        : "";

  const entries = Array.isArray(ocd.ownershipEntries) ? ocd.ownershipEntries : [];

  const mapEntry = (row, isCorporate) => {
    const idPath = isCorporate
      ? row.corporateOwnerIdFiles || row.directorIdFiles
      : row.individualOwnerIdFiles || row.directorIdFiles;
    const poaPath = isCorporate
      ? row.corporateOwnerProofOfAddressFiles || row.directorProofOfAddressFiles
      : row.individualOwnerProofOfAddressFiles || row.directorProofOfAddressFiles;

    const idUrls = parseStoredFileList(idPath);
    const poaUrls = parseStoredFileList(poaPath);

    return {
      fullName: row.name || "",
      percentageOfOwnership:
        typeof row.percentageOfOwnership === "number"
          ? row.percentageOfOwnership
          : row.percentageOfOwnership === ""
            ? ""
            : Number(row.percentageOfOwnership) || "",
      idFile: null,
      proofOfAddressFile: null,
      existingIdUrl: idUrls[0] || "",
      existingProofUrl: poaUrls[0] || "",
    };
  };

  const individualOwners =
    ownershipCompanyType === "individuals"
      ? entries.length
        ? entries.map((row) => mapEntry(row, false))
        : [emptyOwner()]
      : [];

  const corporateUltimateOwners =
    ownershipCompanyType === "corporate"
      ? entries.length
        ? entries.map((row) => mapEntry(row, true))
        : [emptyOwner()]
      : [];

  return {
    confirmed: !!ocd.confirmed,
    ownershipCompanyType,
    directors,
    individualOwners,
    corporateUltimateOwners,
    adminStatus: ocd.status || "",
    adminReason: ocd.reason || "",
  };
}

export function hydrateClientProfileFromMe(raw = {}) {
  const client = raw.client ?? {};
  const ci = client.companyInformation ?? {};
  const pi = client.primaryInformation ?? {};
  const fb = client.financeBilling ?? {};
  const hs = client.healthAndSafetyContact ?? {};
  const ins = client.insurance ?? {};

  const phoneDigits = (raw.phoneNumber || "").replace(/^\+/, "");

  return {
    role: "client",
    primaryInformation: {
      fullName: raw.fullName || "",
      email: raw.email || "",
      phoneNumber: phoneDigits,
      position: pi.position || "",
    },
    companyInformation: {
      registeredCompanyName: ci.registeredCompanyName || "",
      tradingName: ci.tradingName || "",
      companyRegistrationNumber: ci.companyRegistrationNumber || "",
      vatRegistrationNumber: ci.vatRegistrationNumber || "",
      registeredOfficeAddress: ci.registeredOfficeAddress || "",
      tradingAddress: ci.tradingAddress || "",
      organisationType: ci.organisationType || "Ltd",
      industrySector: ci.industrySector || "",
      companyWebsite: ensureCompanyWebsiteProtocol(ci.companyWebsite),
      isWorkplaceDisabilityFriendly: ci.isWorkplaceDisabilityFriendly || "",
    },
    financeBilling: {
      fullName: fb.fullName || "",
      email: fb.email || "",
      phone: (fb.phone || "").replace(/^\+/, ""),
    },
    healthAndSafetyContact: {
      fullName: hs.fullName || "",
      email: hs.email || "",
      phoneNumber: (hs.phoneNumber || "").replace(/^\+/, ""),
    },
    ownershipControlDeclaration: hydrateOwnershipControlDeclaration(
      client.ownershipControlDeclaration
    ),
    insurance: {
      employersLiability: null,
      publicLiability: null,
      existingEmployersLiability: firstStoredFilePath(ins.employersLiability),
      existingPublicLiability: firstStoredFilePath(ins.publicLiability),
      infoAccurate: !!ins.infoAccurate,
      isEmployersLiabilityVerified: !!ins.isEmployersLiabilityVerified,
      isPublicLiabilityVerified: !!ins.isPublicLiabilityVerified,
      employersLiabilityReason: ins.employersLiabilityReason || "",
      publicLiabilityReason: ins.publicLiabilityReason || "",
      employersLiabilityExpiryDate: ins.employersLiabilityExpiryDate || "",
      publicLiabilityExpiryDate: ins.publicLiabilityExpiryDate || "",
    },
  };
}

const withPlus = (d) => (d ? (String(d).startsWith("+") ? d : `+${d}`) : d);

export const CLIENT_PROFILE_SECTIONS = [
  "primaryInformation",
  "companyInformation",
  "financeBilling",
  "healthAndSafetyContact",
  "ownershipControlDeclaration",
  "insurance",
];

function appendOcdUpdateFiles(fd, occ) {
  (occ.directors || []).forEach((d, i) => {
    if (d?.idFile instanceof File) {
      appendOne(
        fd,
        `ownershipControlDeclaration[directors][${i}][directorIdFiles]`,
        d.idFile
      );
    }
    if (d?.proofOfAddressFile instanceof File) {
      appendOne(
        fd,
        `ownershipControlDeclaration[directors][${i}][directorProofOfAddressFiles]`,
        d.proofOfAddressFile
      );
    }
  });

  const ownershipType =
    occ.ownershipCompanyType === "individual"
      ? "individuals"
      : occ.ownershipCompanyType;
  const ownerRows =
    ownershipType === "corporate"
      ? occ.corporateUltimateOwners || []
      : occ.individualOwners || [];
  const idKey =
    ownershipType === "corporate"
      ? "corporateOwnerIdFiles"
      : "individualOwnerIdFiles";
  const poaKey =
    ownershipType === "corporate"
      ? "corporateOwnerProofOfAddressFiles"
      : "individualOwnerProofOfAddressFiles";

  ownerRows.forEach((row, i) => {
    if (row?.idFile instanceof File) {
      appendOne(
        fd,
        `ownershipControlDeclaration[ownershipEntries][${i}][${idKey}]`,
        row.idFile
      );
    }
    if (row?.proofOfAddressFile instanceof File) {
      appendOne(
        fd,
        `ownershipControlDeclaration[ownershipEntries][${i}][${poaKey}]`,
        row.proofOfAddressFile
      );
    }
  });
}

export function buildClientProfileSectionFormData(section, values, options = {}) {
  const { photoFile, photoRemoved } = options;
  const fd = new FormData();
  fd.append("role", "client");

  switch (section) {
    case "primaryInformation": {
      fd.append(
        "primaryInformation",
        JSON.stringify({
          ...values.primaryInformation,
          phoneNumber: withPlus(values.primaryInformation.phoneNumber),
        })
      );
      if (photoFile) {
        fd.append("photo", photoFile);
      } else if (photoRemoved) {
        fd.append("photo", JSON.stringify([]));
      }
      break;
    }
    case "companyInformation": {
      fd.append(
        "companyInformation",
        JSON.stringify({
          ...values.companyInformation,
          companyWebsite: normalizeCompanyWebsiteUrl(
            values.companyInformation.companyWebsite
          ),
        })
      );
      break;
    }
    case "financeBilling": {
      fd.append(
        "financeBilling",
        JSON.stringify({
          fullName: values.financeBilling.fullName,
          email: values.financeBilling.email,
          phone: withPlus(values.financeBilling.phone),
        })
      );
      break;
    }
    case "healthAndSafetyContact": {
      fd.append(
        "healthAndSafetyContact",
        JSON.stringify({
          fullName: values.healthAndSafetyContact.fullName,
          email: values.healthAndSafetyContact.email,
          phoneNumber: withPlus(values.healthAndSafetyContact.phoneNumber),
        })
      );
      break;
    }
    case "ownershipControlDeclaration": {
      const occ = values.ownershipControlDeclaration;
      const wireOwnership = buildWireOwnershipControlDeclaration(occ, {
        forUpdate: true,
      });
      fd.append("ownershipControlDeclaration", JSON.stringify(wireOwnership));
      appendOcdUpdateFiles(fd, occ);
      break;
    }
    case "insurance": {
      fd.append("insurance", JSON.stringify({}));
      if (values.insurance.employersLiability instanceof File) {
        appendOne(
          fd,
          "insuranceEmployersliability",
          values.insurance.employersLiability
        );
      }
      if (values.insurance.publicLiability instanceof File) {
        appendOne(
          fd,
          "insurancePublicliability",
          values.insurance.publicLiability
        );
      }
      break;
    }
    default:
      break;
  }

  return fd;
}

export function buildClientProfileFormData(values, options = {}) {
  const fd = new FormData();
  fd.append("role", "client");
  CLIENT_PROFILE_SECTIONS.forEach((section) => {
    const sectionFd = buildClientProfileSectionFormData(section, values, options);
    for (const [key, value] of sectionFd.entries()) {
      if (key !== "role") {
        fd.append(key, value);
      }
    }
  });
  return fd;
}

export function hasNewClientProfileFiles(values, { photoFile } = {}) {
  if (photoFile) return true;
  const ins = values.insurance || {};
  if (ins.employersLiability instanceof File) return true;
  if (ins.publicLiability instanceof File) return true;

  const occ = values.ownershipControlDeclaration || {};
  for (const d of occ.directors || []) {
    if (d.idFile instanceof File || d.proofOfAddressFile instanceof File) return true;
  }
  const rows =
    occ.ownershipCompanyType === "corporate"
      ? occ.corporateUltimateOwners
      : occ.individualOwners;
  for (const r of rows || []) {
    if (r.idFile instanceof File || r.proofOfAddressFile instanceof File) return true;
  }
  return false;
}
